import React, {
  useState, useMemo, useCallback, useEffect,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { dateFilterColumn } from '../../utils/dateFilterColumn';
import {
  useCompCyclesApi, useCycleDetail, useMyTeamCycles, useAllPeriodsMap,
  STATUS_COLORS, StatusBadge,
} from './config';
import NewCycleModal from './components/NewCycleModal';
import CycleDetailView from './components/CycleDetailView';
import MyTeamTab from './MyTeamTab';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Tabs from '../../components/ui/Tabs';

const Container = styled.div``;

const EmptyCard = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 40px 24px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

// Faz G (spec 2026-08-08 §3/§4, Task 4) — Ücret Artış Turu tek sayfası: İK
// sekmesi (tur listesi + sihirbaz + detay + kapanış/export, PM yazma yetkisi
// VE enterpriseAdmin gerektirir — Faz 1 / Görev 4 (PMB-02) sunucu tarafında
// tüm ücret turu uçlarını enterpriseAdmin kapısına aldı; sekmeyi yalnız
// PM.Write ile açmak, kullanıcıya her tıklamada 403 toast'ı gösteren bir UX
// regresyonu olurdu) ve Ekibim sekmesi (self-servis
// yönetici, my-team ucundan EN AZ BİR satır dönerse görünür — task-3-report.md
// §6 "boş grup filtrelenir"). İkisi de yoksa (PM.Read taşıyan ama İK yazma
// yetkisi olmayan ve hiçbir doğrudan bağlısı bu turlarda olmayan bir kullanıcı
// — ör. route privileges['PM.Read'] menüde görünürlüğü sağlar ama sayfa
// İÇİNDE ek bir gate daha var) bilgilendirici boş-durum gösterilir.
//
// hrVisible SENKRON (canWrite — AuthContext'ten anında), myTeamVisible ise
// bir GET'in tamamlanmasını bekler. hrVisible=true olan kullanıcı için
// myTeam fetch'i ARKA PLANDA sürer — İK sekmesi ilk render'da hemen
// kullanılabilir, Ekibim sekmesi veri gelince sessizce eklenir. hrVisible
// false ise (safi yönetici) my-team fetch'i tamamlanana dek yükleniyor
// gösterilir (aksi halde "boş-durum" ile "henüz yüklenmedi" ayrımı yapılamaz).
export default function CompCyclesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { canWrite, isHr } = useAuth();
  const hrVisible = canWrite('PM') && Boolean(isHr);

  // PMF-05 — İK YÜZEYİNİN uçları hrVisible olmadan HİÇ çağrılmaz. Eskiden
  // koşulsuz çağrılıyorlardı: v1/comp-cycles sınıf düzeyinde PM.Write + servis
  // içinde enterpriseAdmin kapısı taşıdığı için, yalnız "Ekibim" sekmesini
  // kullanan bir yönetici sayfayı her açtığında 403 hata toast'ı görüyordu.
  // (Menüye requiresEnterpriseAdmin koymak ÇÖZÜM DEĞİL — o, self-servis
  // "Ekibim" sekmesinin web'deki TEK giriş noktasını yok ederdi; ayrıntı
  // config/index.js useCompCyclesApi yorumunda.)
  const {
    data, fetching, createCycle, runLifecycleAction, deleteDraftCycle, exportCycle,
  } = useCompCyclesApi(hrVisible);
  const periodsMap = useAllPeriodsMap(hrVisible);

  const {
    cycles: myTeamCycles, fetching: myTeamFetching, proposeRecommendation,
  } = useMyTeamCycles();
  const myTeamVisible = !myTeamFetching && myTeamCycles.length > 0;

  const tabs = useMemo(() => {
    const list = [];
    if (hrVisible) list.push({ key: 'hr', label: t('compCycles.tabs.hr') });
    if (myTeamVisible) list.push({ key: 'myTeam', label: t('compCycles.tabs.myTeam') });
    return list;
  }, [hrVisible, myTeamVisible, t]);

  const [activeTab, setActiveTab] = useState(null);
  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((tb) => tb.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs, activeTab]);

  // İK sekmesi kendi içinde liste/detay görünümü arasında geçiş yapar (tek
  // sayfa mimarisi — spec §4 "ayrı sayfa YOK", brief "FE tek sayfa iki
  // sekme"). Ayrı bir /:id rotası YOK, calibration-sessions'ın List+Grid
  // ikilisinin AKSİNE.
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [lifecycleConfirm, setLifecycleConfirm] = useState({
    isOpen: false, verb: null, rowId: null, title: '', message: '',
  });
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  const {
    detail, fetching: detailFetching, refresh: refreshDetail, finalizeRecommendation, closeCycle,
  } = useCycleDetail(selectedCycleId);

  const openDetail = useCallback((row) => setSelectedCycleId(row.id), []);
  const backToList = useCallback(() => setSelectedCycleId(null), []);

  // Detaydan tetiklenen yaşam döngüsü aksiyonları (review'e al/iptal) HEM
  // detayı HEM liste arka planındaki verinin bayatlamasını önlemek için
  // listeyi de tazeler (calibration-sessions'ın grid+liste ikilisinin AKSİNE
  // burada tek sayfa olduğundan iki state — detail + list — elle senkron
  // tutulmalı).
  const runLifecycleFromDetail = useCallback(async (id, verb) => {
    const ok = await runLifecycleAction(id, verb);
    refreshDetail();
    return ok;
  }, [runLifecycleAction, refreshDetail]);

  const handleCloseFromDetail = useCallback(async () => {
    const result = await closeCycle();
    return result;
  }, [closeCycle]);

  const openLifecycleConfirm = useCallback((verb, row) => {
    const messageKey = verb === 'activate'
      ? 'compCycles.activateConfirm.message'
      : 'compCycles.cancelConfirm.message';
    setLifecycleConfirm({
      isOpen: true,
      verb,
      rowId: row.id,
      title: t(`compCycles.actions.${verb}`),
      message: t(messageKey),
    });
  }, [t]);

  const handleLifecycleConfirm = useCallback(async () => {
    setLifecycleLoading(true);
    await runLifecycleAction(lifecycleConfirm.rowId, lifecycleConfirm.verb);
    setLifecycleConfirm({
      isOpen: false, verb: null, rowId: null, title: '', message: '',
    });
    setLifecycleLoading(false);
  }, [runLifecycleAction, lifecycleConfirm]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await deleteDraftCycle(deleteConfirm.id);
    setDeleteConfirm({ isOpen: false, id: null, name: '' });
    setDeleting(false);
  }, [deleteDraftCycle, deleteConfirm.id]);

  const handleExport = useCallback((cycle) => exportCycle(cycle.id, cycle.name), [exportCycle]);

  const buildActionOptions = useCallback((row) => {
    const actions = [{ type: 'details', label: t('compCycles.actions.viewDetail'), onClick: openDetail }];
    if (row.status === 'draft') {
      actions.push({ type: 'submit_for_approval', label: t('compCycles.actions.activate'), onClick: (r) => openLifecycleConfirm('activate', r) });
      actions.push({
        type: 'delete',
        label: t('common.delete'),
        onClick: (r) => setDeleteConfirm({ isOpen: true, id: r.id, name: r.name }),
      });
    }
    if (['draft', 'active', 'review'].includes(row.status)) {
      actions.push({ type: 'cancel', label: t('compCycles.actions.cancel'), onClick: (r) => openLifecycleConfirm('cancel', r) });
    }
    actions.push({ type: 'download', label: t('compCycles.actions.export'), onClick: handleExport });
    return actions;
  }, [t, openDetail, openLifecycleConfirm, handleExport]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('compCycles.fields.name'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'periodId',
      label: t('compCycles.fields.period'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => periodsMap[v] || '-',
      },
    },
    {
      name: 'cycleType',
      label: t('compCycles.fields.type'),
      options: {
        filter: true, sort: false,
        customBodyRender: (v) => t(`compCycles.type.${v}`, { defaultValue: v }),
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: true, sort: false,
        customBodyRender: (v) => {
          const cfg = STATUS_COLORS[v] || STATUS_COLORS.draft;
          return (
            <StatusBadge color={cfg.color} bg={cfg.bg}>
              {t(`compCycles.status.${v}`, { defaultValue: v })}
            </StatusBadge>
          );
        },
      },
    },
    // Tablo istemci tarafında (serverSide={false}) — süzgeç tarayıcıda uygulanmalı.
    dateFilterColumn('createDate', t('compCycles.fields.createDate'), lang, { clientSide: true }),
  ], [t, lang, periodsMap]);

  const hrTabContent = (
    <>
      {modalOpen && (
        <NewCycleModal
          onClose={() => setModalOpen(false)}
          onCreate={createCycle}
        />
      )}

      <ConfirmDialog
        isOpen={lifecycleConfirm.isOpen}
        title={lifecycleConfirm.title}
        message={lifecycleConfirm.message}
        onConfirm={handleLifecycleConfirm}
        onCancel={() => setLifecycleConfirm({
          isOpen: false, verb: null, rowId: null, title: '', message: '',
        })}
        loading={lifecycleLoading}
        type={lifecycleConfirm.verb === 'cancel' ? 'danger' : 'info'}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('compCycles.deleteConfirm.title')}
        message={t('compCycles.deleteConfirm.message', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={deleting}
        type="danger"
      />

      {selectedCycleId ? (
        <CycleDetailView
          cycle={detail}
          fetching={detailFetching}
          onBack={backToList}
          onRunLifecycle={runLifecycleFromDetail}
          onFinalize={finalizeRecommendation}
          onCloseCycle={handleCloseFromDetail}
          onExport={handleExport}
        />
      ) : (
        <DataTable
          title={t('compCycles.title')}
          fetching={fetching}
          columns={[
            ...columns,
            {
              name: 'actions',
              label: ' ',
              options: {
                filter: false, sort: false, download: false, print: false,
                customBodyRender: (_, tableMeta) => {
                  const row = data[tableMeta.rowIndex];
                  if (!row) return null;
                  return <OptionsMenu actions={buildActionOptions(row)} row={row} />;
                },
              },
            },
          ]}
          data={data}
          serverSide={false}
          canCreateNewRecord
          newButtonLabel={t('compCycles.new')}
          onNewButtonClick={() => setModalOpen(true)}
          onRowClick={openDetail}
        />
      )}
    </>
  );

  const showLoading = !hrVisible && myTeamFetching;

  if (showLoading) {
    return <Container><EmptyCard>{t('common.loading')}</EmptyCard></Container>;
  }

  if (tabs.length === 0) {
    return <Container><EmptyCard>{t('compCycles.noAccess')}</EmptyCard></Container>;
  }

  return (
    <Container>
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'hr' && hrVisible && hrTabContent}

      {activeTab === 'myTeam' && myTeamVisible && (
        <MyTeamTab cycles={myTeamCycles} proposeRecommendation={proposeRecommendation} />
      )}
    </Container>
  );
}
